import { z } from "zod";

/**
 * An API-safe inference-upload row (`inferenceUpload.getStatus`/`list`), from
 * `serializeUpload`. Notable serialization quirks (verified against
 * `InferenceUploadsTable` + `serialize.ts`):
 *   - `multipartUploadId` (an R2 secret) is STRIPPED — never present.
 *   - `createdAt`/`completedAt`/`archivedAt` are `.toISOString()`'d → STRINGS.
 *   - `lastActivityAt` is NOT serialized, so it stays a raw `Date | null`
 *     (mixed string/Date in the same object — a deliberate landmine).
 *   - `totalLines`/`processedLines`/`failedLines` are `bigint` columns in
 *     `mode: "number"` → JS numbers, not strings.
 *   - `errors` is an untyped jsonb array (`{line,error}[]` by convention, no
 *     `$type`) → `z.array(z.unknown())`; `status`/`detectedFormat` are free text.
 */
export const serializedInferenceUploadSchema = z.object({
  id: z.string(),
  teamId: z.string(),
  projectId: z.string(),
  taskId: z.string().nullable(),
  name: z.string(),
  status: z.string(),
  r2Key: z.string(),
  totalLines: z.number(),
  processedLines: z.number(),
  failedLines: z.number(),
  // Untyped jsonb column (`{line,error}[]` by convention, no Drizzle `$type`),
  // so the resolver's static type is `unknown` — mirror with `z.unknown()`
  // (not `z.array(...)`, which wouldn't be assignable from `unknown`).
  errors: z.unknown(),
  detectedFormat: z.string().nullable(),
  statusDetail: z.string().nullable(),
  lastActivityAt: z.date().nullable(),
  createdBy: z.string(),
  createdAt: z.string(),
  completedAt: z.string().nullable(),
  archivedAt: z.string().nullable(),
});

/** Paginated upload list (`inferenceUpload.list`). */
export const inferenceUploadListSchema = z.object({
  items: z.array(serializedInferenceUploadSchema),
  hasMore: z.boolean(),
  totalCount: z.number(),
});

/** Presigned download for a completed upload (`inferenceUpload.getDownloadUrl`). */
export const inferenceUploadDownloadUrlSchema = z.object({
  downloadUrl: z.string(),
});
